import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { LogoMarque } from '../marque/LogoMarque';
import { useAuth } from '../auth/useAuth';
import { ErreurApi } from '../lib/api';
import { useMarque } from '../marque/useMarque';
import { useTitrePage } from '../lib/useTitrePage';

/** Message affiché après un retour d'échec de « Se connecter avec Google ». */
const MESSAGES_ERREUR_GOOGLE: Record<string, string> = {
  compte_introuvable: 'Aucun compte associé à cet email Google. Contactez l’administration.',
  google_indisponible: 'Connexion Google indisponible pour le moment. Utilisez votre mot de passe.',
  google_erreur: 'La connexion Google a échoué. Réessayez ou utilisez votre mot de passe.',
};

/**
 * Page de connexion, reprise de la maquette validée.
 *
 * Écart assumé par rapport au prototype, à valider avec le client : le
 * sélecteur de profil du prototype servait à simuler les dix espaces ; en
 * production le profil vient du compte, il n'est pas choisi à l'écran.
 *
 * Seul Google est proposé (dérogation explicite) comme fournisseur d'identité
 * délégué, sur la base d'un compte déjà existant — il n'en crée jamais. Le
 * bouton Microsoft de la maquette, jamais branché, est retiré (29/10/2026).
 */
export function LoginPage(): JSX.Element {
  useTitrePage('Connexion');
  const { utilisateur, connexion } = useAuth();
  const { marque } = useMarque();
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    const parametres = new URLSearchParams(window.location.search);
    const code = parametres.get('erreur');
    if (code) {
      setErreur(MESSAGES_ERREUR_GOOGLE[code] ?? 'La connexion Google a échoué.');
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  if (utilisateur) return <Navigate to="/dashboard" replace />;

  async function soumettre(evenement: React.FormEvent): Promise<void> {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await connexion(email, motDePasse);
    } catch (cause) {
      setErreur(
        cause instanceof ErreurApi
          ? cause.message
          : 'Connexion impossible. Vérifiez votre accès au réseau.',
      );
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="login-bg flex min-h-screen items-center justify-center p-4 sm:p-6">
      <div className="grid grid-cols-1 w-full max-w-5xl items-center gap-8 lg:grid-cols-2">
        {/* Volet de marque */}
        <div className="hidden space-y-8 text-white lg:block">
          <div className="inline-flex rounded-2xl bg-white px-4 py-3 shadow-lg">
            <LogoMarque className="h-9 w-auto" />
          </div>

          <div className="space-y-4">
            {/* Accroche, pas titre : le volet est masqué sur téléphone, le titre
                de la page reste « Bienvenue. » partout (RGAA 9.1). */}
            <p className="font-display text-4xl font-extrabold leading-tight xl:text-5xl">
              La plateforme de gestion
              <br />
              <span className="text-codialis-mint">de votre CFA et de votre OF.</span>
            </p>
            <p className="max-w-md text-lg text-white/70">
              Un seul dossier du premier entretien au suivi post-formation. Documents
              réglementaires générés, alertes automatiques, gestion multi-antennes unifiée.
            </p>
          </div>

          <div className="grid max-w-md grid-cols-2 gap-4">
            {[
              ['Multi-antennes', 'Périmètres de sites cloisonnés'],
              ['Saisie unique', 'CERFA, SIFA et conventions générés'],
              ['UTC+4', 'Échéances calculées à l’heure de La Réunion'],
              ['RGPD', 'Données hébergées en France, accès tracés'],
            ].map(([titre, sousTitre]) => (
              <div key={titre} className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-xl font-bold text-codialis-mint">{titre}</div>
                <div className="text-xs text-white/60">{sousTitre}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Formulaire */}
        <div className="rounded-3xl bg-white p-4 sm:p-8 shadow-2xl lg:p-10">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <LogoMarque className="h-8 w-auto" />
          </div>

          <div className="mb-8">
            <h1 className="font-display text-2xl font-bold text-codialis-navy">Bienvenue.</h1>
            <p className="mt-1 text-sm text-codialis-gray">
              Connectez-vous à votre espace de travail.
            </p>
          </div>

          <form onSubmit={soumettre} className="space-y-4" noValidate>
            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-codialis-navy"
              >
                Adresse e-mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(evenement) => setEmail(evenement.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-codialis-mint"
              />
            </div>

            <div>
              <label
                htmlFor="motdepasse"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-codialis-navy"
              >
                Mot de passe
              </label>
              <input
                id="motdepasse"
                type="password"
                autoComplete="current-password"
                required
                value={motDePasse}
                onChange={(evenement) => setMotDePasse(evenement.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-codialis-mint"
              />
            </div>

            {erreur && (
              <div
                role="alert"
                className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {erreur}
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-codialis-gray">
                <input type="checkbox" className="rounded text-codialis-mint" />
                Se souvenir de moi
              </label>
              <Link to="/mot-de-passe-oublie" className="font-medium text-codialis-sky hover:underline">
                Mot de passe oublié ?
              </Link>
            </div>

            <button
              type="submit"
              disabled={enCours}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-codialis-navy py-3.5 font-semibold text-white transition hover:bg-codialis-deep disabled:cursor-not-allowed disabled:opacity-60"
            >
              {enCours ? 'Connexion…' : 'Accéder à mon espace'}
              {!enCours && (
                <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              )}
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-codialis-gray">Ou continuer avec</span>
              </div>
            </div>

            <a
              href="/api/auth/google/connexion"
              className="flex w-full items-center justify-center rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-codialis-navy transition hover:bg-gray-50"
            >
              Google
            </a>
          </form>

          <p className="mt-8 text-center text-xs text-codialis-gray">
            {marque.nom} · Données hébergées en France
          </p>
        </div>
      </div>
    </div>
  );
}
